'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { Button } from '@7f-ui/components/button/button';
import { useResizeObserverRef } from '@7f-ui/hooks/use-resize-observer-ref';
import { shopAsset } from '@utils/utils-config';
import { createGermanRelativeTimeFormatter } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { differenceInCalendarDays, differenceInCalendarMonths, differenceInCalendarYears } from 'date-fns';
import Image from 'next/image';
import { useState } from 'react';
import { RatingStars } from './rating-stars';
type ExperienceReviewCardProps = {
  authorName: string;
  rating: number;
  submittedAt: string;
  text: string;
  /**
   * Cut long texts after five lines with a "Mehr anzeigen" hint (reviews section);
   * the surrounding button opens the full review. Full text without.
   */
  clamp?: boolean;
};
export function ExperienceReviewCard(t0) {
  const $ = _c(28);
  const {
    authorName,
    rating,
    submittedAt,
    text,
    clamp: t1
  } = t0;
  const clamp = t1 === undefined ? false : t1;
  const [isTextCut, setIsTextCut] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const [entry] = t3;
      setIsTextCut(entry.target.scrollHeight > entry.target.clientHeight);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [textRef] = useResizeObserverRef(t2);
  let t3;
  if ($[1] !== submittedAt) {
    const submittedDate = new Date(submittedAt);
    t3 = formatReviewDate(submittedDate, createGermanRelativeTimeFormatter({
      numeric: "auto"
    }));
    $[1] = submittedAt;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const relativeDate = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Image src={shopAsset("/images/guide-placeholder.png")} alt="Avatar" width={40} height={40} className="size-10 shrink-0 rounded-xl" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== authorName) {
    t5 = <p className="truncate font-base-copy font-semibold">{authorName}</p>;
    $[4] = authorName;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== relativeDate || $[7] !== submittedAt) {
    t6 = <time dateTime={submittedAt} className="block font-base-copy text-secondary-text first-letter:uppercase">{relativeDate}</time>;
    $[6] = relativeDate;
    $[7] = submittedAt;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-2">{t4}<div className="min-w-0">{t5}{t6}</div></div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `${rating} von 5 Sternen`;
  let t9;
  if ($[12] !== rating) {
    t9 = <RatingStars rating={rating} />;
    $[12] = rating;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <div role="img" aria-label={t8} className="mt-1 flex shrink-0 sm:mt-0">{t9}</div>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7) {
    t11 = <div className="flex items-start justify-between gap-2">{t7}{t10}</div>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== clamp || $[21] !== isTextCut || $[22] !== text || $[23] !== textRef) {
    t12 = text.trim() ? <div className="grid justify-items-start gap-2"><p ref={clamp ? textRef : undefined} data-testid={shopTestId("ExperienceReviewCard.comment")} className={cx("wrap-break-word whitespace-pre-line font-body-copy", {
        "line-clamp-5": clamp
      })}>{text}</p>{clamp && isTextCut && <Button variant="underline" as="span" aria-hidden={true}>Mehr anzeigen</Button>}</div> : null;
    $[20] = clamp;
    $[21] = isTextCut;
    $[22] = text;
    $[23] = textRef;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = <article className="flex h-full min-w-0 flex-col gap-6 rounded-2xl border border-primary/15 bg-white p-6 sm:gap-4 sm:rounded-lg sm:p-4">{t11}{t12}</article>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}
function formatReviewDate(date: Date, formatter: Intl.RelativeTimeFormat) {
  const now = new Date();
  const days = differenceInCalendarDays(date, now);
  const absoluteDays = Math.abs(days);
  if (absoluteDays < 7) return formatter.format(days, 'day');
  if (absoluteDays < 31) return formatter.format(Math.round(days / 7), 'week');
  if (absoluteDays < 365) {
    return formatter.format(differenceInCalendarMonths(date, now), 'month');
  }
  return formatter.format(differenceInCalendarYears(date, now), 'year');
}